import React, { useState, useEffect } from "react";
import {
  TableCell,
  styled,
  CircularProgress,
  Divider,
  Stack,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import styles from "./styles";
import { usePackageContext, useProposalContext } from "../..";
import { useNavigate } from "react-router-dom";
import { getApiData } from "../../../../utils/APIHelper";
import { toast } from "react-toastify";
import ConfirmModel from "../../../../components/ConfirmModel";
import { MdOutlineEdit, MdOutlineDelete } from "react-icons/md";
import PackageGroupModal from "../../../../components/PackageGroupModal";
import { ReactComponent as ArrowIcon } from "../../../../assets/svg/ArrowIcon.svg";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"; // Add this if missing
import {
  getImageSrc,
  formattedAmountWithoutAed,
} from "../../../../utils/CommonFunction";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as SmallDhiramIcon } from "../../../../assets/svg/SmallDhiramIcon.svg";
import { renderCurrencyIcon } from "../../../../utils/CurrencyUtils";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  textTransform: "capitalize",
  padding: "0 !important",
}));

const AddStyledButton = styled(Button)(({ theme }) => ({
  padding: ".5vh 1vw !important",
  boxShadow: "none !important",
  border: "none",
  fontSize: "16px !important",
  borderRadius: "10px !important",
  letterSpacing: "0",
  fontWeight: "500 !important",
  whiteSpace: "nowrap",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

export default function PackageGroups() {
  const { packageDetails, setPackageDetails } = usePackageContext();
  const [packageGroups, setPackageGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedDeleteGroup, setSelectedDeleteGroup] = useState(null);
  const [loader, setLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [expandedInclusions, setExpandedInclusions] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (packageDetails && packageDetails?.id) {
      getDetailsApiCall(packageDetails?.id);
      getGroupDetailsApiCall(packageDetails?.id);
    }
  }, [packageDetails?.id]);

  async function getGroupDetailsApiCall(id) {
    setLoader(true);
    try {
      const response = await getApiData(
        `api/v2/package/${id}/groups`,
        "GET",
        {},
        { "Content-Type": "application/json" },
        true
      );
      if (response) {
        setPackageGroups(response);
        setExpandedInclusions(response.map((group) => group.id));
      }
      setLoader(false);
    } catch (error) {
      console.log("error:===>>>", error);
      setLoader(false);
    }
  }

  async function getDetailsApiCall(id) {
    setLoader(true);
    try {
      const response = await getApiData(
        `api/v2/package/${id}`,
        "GET",
        {},
        { "Content-Type": "application/json" },
        true
      );
      if (response) {
        setPackageDetails(response);
        setLoader(false);
      } else {
        toast.error("Oops! Something went wrong");
      }
    } catch (error) {
      toast.error(error.toString());
    }
  }

  const handlePrevious = () => {
    navigate(`/new-create-package/${packageDetails?.id}/inclusion`);
  };

  const handleNext = () => {
    navigate(`/new-create-package/${packageDetails?.id}/images`);
  };

  const handleCloseModal = () => setSelectedGroup(null);

  const handleModalClose = () => {
    navigate("/proposals");
  };

  async function handleConfirm() {
    console.log("selectedDeleteGroup", selectedDeleteGroup);
    try {
      const response = await getApiData(
        `api/v2/package/${packageDetails?.id}/groups/${selectedDeleteGroup?.id}`,
        "DELETE",
        {},
        { "Content-Type": "application/json" },
        true
      );
      toast.success("Package Deleted Successfully");
      setSelectedDeleteGroup(null);
      getGroupDetailsApiCall(packageDetails?.id);
    } catch (error) {
      console.log("err===>", error);
    }
  }

  const isAllInclusionsLinked = () => {
    // Check if inclusions exist and are not empty
    if (!packageDetails?.inclusions || packageDetails?.inclusions?.length === 0) {
      return true; // Disable button if no inclusions
    }

    const allGroupInclusionIds = new Set(
      packageGroups?.flatMap((group) =>
        group?.inclusions?.map((incl) => incl.id)
      )
    );

    return packageDetails?.inclusions?.every((incl) =>
      allGroupInclusionIds.has(incl.id)
    );
  };

  const toggleMilestone = (groupId) => {
    setExpandedInclusions((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  return (
    <div style={{ ...styles.container, ...styles.columnFlex, gap: "15px" }}>
      <h3 style={styles.heading}>Package Groups</h3>

      {loader ? (
        <center>
          <CircularProgress />
        </center>
      ) : (
        <div style={{ ...styles.columnFlex }}>
          {packageGroups?.map((item) => (
            <div key={item.id}>
              <div
                style={{
                  ...styles.rowFlex,
                  justifyContent: "space-between",
                  borderRadius: "12px",
                  background: "#F7F7F7",
                  padding: "1vh 1vw",
                  alignItems: "center",
                }}
              >
                <div style={{ ...styles.rowFlex, flex: 1 }}>
                  <Box display="flex" alignItems="center">
                    <span style={styles.small_sub_heading}>{item?.name}</span>
                  </Box>
                </div>

                <div style={{ ...styles.columnFlex, flex: 1 }}>
                  <h3 style={styles.small_text}>Group By</h3>
                  <div style={styles.small_sub_heading}>{item?.groupBy}</div>
                </div>

                <div
                  style={{
                    ...styles.rowFlex,
                    flex: 0.3,
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  <MdOutlineEdit
                    onClick={() => setSelectedGroup(item)}
                    style={{
                      cursor: "pointer",
                      fontSize: "1.3rem",
                      color: "#0D0D0E",
                    }}
                  />
                  <MdOutlineDelete
                    style={{
                      cursor: "pointer",
                      fontSize: "1.3rem",
                      color: "#e54746",
                    }}
                    onClick={() => setSelectedDeleteGroup(item)}
                  />
                  <IconButton onClick={() => toggleMilestone(item.id)}>
                    {expandedInclusions.includes(item.id) ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                </div>
              </div>
              {console.log(
                "expandedInclusions",
                expandedInclusions,
                "item",
                item
              )}

              {expandedInclusions.includes(item.id) && (
                <div
                  style={{
                    ...styles.columnFlex,
                    ...styles.budgetContainer,
                    gap: "15px",
                    marginBottom: "2vh",
                  }}
                >
                  {item?.inclusions?.map((budgetItem) => (
                    <div
                      key={budgetItem.id}
                      style={{
                        ...styles.rowFlex,
                        gap: "0px",
                        alignItems: "flex-start",
                      }}
                    >
                      {console.log("bud", budgetItem)}
                      <ArrowIcon />
                      <div style={styles.budgetRow}>
                        <div style={{ ...styles.rowFlex, flex: 1 }}>
                          <Box display="flex" alignItems="center">
                            <Box
                              component="img"
                              src={getImageSrc(budgetItem?.image?.path)}
                              alt="Inclusion Icon"
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                border: "2px solid white",
                                boxShadow: "0px 4px 2.67px 0px #00000026",
                                marginRight: 1,
                              }}
                            />
                            <span style={styles.small_sub_heading}>
                              {budgetItem?.name}
                            </span>
                          </Box>
                        </div>

                        <div style={{ ...styles.rowFlex, flex: 1 }}>
                          <div style={styles.small_text}>
                            <div
                              style={{
                                ...styles.rowFlex,
                                ...styles.small_sub_heading,
                              }}
                            >
                              <NewDesignIcon />
                              {budgetItem?.materialType}
                            </div>
                          </div>
                        </div>

                        <div style={{ ...styles.columnFlex, flex: 1 }}>
                          <h3 style={styles.small_text}>Units</h3>
                          <div style={styles.small_sub_heading}>
                            {budgetItem?.materialUnit}
                          </div>
                        </div>

                        <div style={{ ...styles.columnFlex, flex: 1 }}>
                          <h3 style={styles.small_text}>Cost</h3>
                          <div
                            style={{
                              ...styles.small_sub_heading,
                              ...styles.rowFlex,
                              gap: "5px",
                            }}
                          >
                            {budgetItem?.cost && renderCurrencyIcon('small')}
                            {budgetItem?.cost
                              ? formattedAmountWithoutAed(budgetItem?.cost)
                              : "-"}
                          </div>
                        </div>

                        <div style={{ ...styles.columnFlex, flex: 1 }}>
                          <h3 style={styles.small_text}>Description</h3>
                          <div style={styles.small_sub_heading}>
                            {budgetItem?.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <StyledTableCell>
        <AddStyledButton
          onClick={() => setSelectedGroup({})}
          style={
            isAllInclusionsLinked() ? styles.disabledBtn : styles.milestoneBtn
          }
          disabled={isAllInclusionsLinked()}
        >
          + Add Package Group
        </AddStyledButton>
      </StyledTableCell>

      {selectedGroup && (
        <PackageGroupModal
          isCreationOpen={Boolean(selectedGroup)}
          visible={Boolean(selectedGroup)}
          handleCloseCreation={handleCloseModal}
          selectedPackageGroup={selectedGroup}
          isEdit={Object.keys(selectedGroup).length !== 0}
          inclusions={packageDetails?.inclusions}
          packageGroups={packageGroups}
          packageId={packageDetails?.id}
          getProjectDetails={getGroupDetailsApiCall}
        />
      )}

      {selectedDeleteGroup && (
        <ConfirmModel
          visible={!!selectedDeleteGroup}
          handleClose={() => setSelectedDeleteGroup(null)}
          confirmation={handleConfirm}
          message={`Are you sure you want to delete ${selectedDeleteGroup?.name} group?`}
        />
      )}

      <Divider sx={{ width: "100%" }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <GreyStyledButton onClick={handlePrevious}>
          {buttonLoader ? (
            <CircularProgress size={26} style={{ color: "#fff" }} />
          ) : (
            "Previous Step"
          )}
        </GreyStyledButton>

        <Stack direction="row" justifyContent="flex-end" gap="10px">
          <GreyStyledButton onClick={handleModalClose}>Cancel</GreyStyledButton>
          <StyledButton variant="contained" onClick={handleNext}>
            {buttonLoader ? (
              <CircularProgress size={26} style={{ color: "#fff" }} />
            ) : (
              "Save & Continue"
            )}
          </StyledButton>
        </Stack>
      </Stack>
    </div>
  );
}
